'use client';

import AuthPhoneInput from '@/components/AuthPhoneInput';
import { DEFAULT_OTP_RESET_COUNTDOWN, OTPForm } from '@/components/OTP';
import { PHONE_REGEX } from '@/utils/regex';
import { useToasts } from '@medsimples/design-system';
import {
  Box,
  FormControl,
  FormLabel,
  Text,
  VStack,
} from '@medsimples/design-system/src/chakra';
import { Button } from '@medsimples/design-system/src/components';
import { SendSmsOtpRequest } from '@medsimples/doctor-onboarding-openapi-v2';
import { capitalize } from 'lodash';
import { useRouter } from 'next/navigation';
import { type FormEvent, useEffect, useState } from 'react';
import useCountDown from 'react-countdown-hook';
import { doctorOnboardingAPIV2 } from '../../../api';
import BackButton from '../../../components/BackButton';
import { SigninMethodLabel } from '../../../utils/constants';
import { useDesignTokens } from '../../providers/design_tokens_provider';
import { handleOTPError } from '../auth';

export default function Signup() {
  const tokens = useDesignTokens();
  const { errorToast } = useToasts();
  const router = useRouter();
  const [timeLeft, { start, reset }] = useCountDown(
    DEFAULT_OTP_RESET_COUNTDOWN,
  );
  const [phone, setPhone] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [_isClient, setIsClient] = useState(false);
  const [isWaitingOTP, setIsWaitingOTP] = useState(false);

  // FIXME: Temporary Next.js Hydration error supression
  useEffect(() => {
    setIsClient(true);
  }, []);

  if (isWaitingOTP) {
    return (
      <OTPForm
        onClickResend={sendSignup}
        onClickVerify={onClickVerify}
        timeLeft={timeLeft}
        onBackButtonAction={onBackButton}
      />
    );
  }

  return (
    <>
      <form
        style={{ display: 'contents' }}
        onSubmit={onSendSignup}
        noValidate={true}
      >
        <Box marginBottom={12}>
          <Text textAlign='center' fontSize='xl'>
            Insira seu {SigninMethodLabel.phone} abaixo
          </Text>
        </Box>

        <Box marginBottom={12} w='100%'>
          <FormControl isRequired>
            <FormLabel>
              <Text
                fontSize='0.8em'
                color={tokens.text.secondary.color}
                fontWeight='bold'
                display='inline-block'
              >
                {capitalize(SigninMethodLabel.phone)}
              </Text>
            </FormLabel>
            <AuthPhoneInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </FormControl>
        </Box>

        <Box w='100%'>
          <VStack spacing={5}>
            <Button
              colorScheme={tokens.button.primary.scheme}
              data-testid='signup-continue-button'
              isLoading={isLoading}
              type='submit'
            >
              Continuar
            </Button>

            <BackButton router={router} action={onBackButton} />
          </VStack>
        </Box>
      </form>
    </>
  );

  function onBackButton() {
    if (isWaitingOTP) {
      setIsWaitingOTP(false);
      return;
    }

    router.back();
    router.refresh();
  }

  async function sendSignup() {
    setLoading(true);

    // Send OTP to the user
    await doctorOnboardingAPIV2()
      .user.sendSignup({
        type: SendSmsOtpRequest.type.SMS_OTP,
        phone: phone.replace(PHONE_REGEX, ''),
      })
      .then((res) => {
        if (!res.success) {
          throw 'Ocorreu um erro inesperado!';
        }

        setIsWaitingOTP(true);
        start();
      })
      .catch((err) => {
        console.error(err);
        const [title, msg] = handleOTPError(err);
        errorToast(msg, title);
      })
      .finally(() => setLoading(false));
  }

  async function onSendSignup(e: FormEvent) {
    e.preventDefault();
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const form: any = e.target;
    if (!form.checkValidity()) {
      errorToast(
        'Atenção',
        'Verifique se todos os campos foram preenchidos corretamente',
      );
      return;
    }
    await sendSignup();
  }

  async function onClickVerify(code: string) {
    if (!code || code.length < 6) {
      errorToast('Erro: insira o código de verificação corretamente');
      return;
    }

    setLoading(true);
    await doctorOnboardingAPIV2()
      .user.signup({
        code,
        phone: phone.replace(PHONE_REGEX, ''),
        type: SendSmsOtpRequest.type.SMS_OTP,
      })
      .then((res) => {
        if (!res?.success) {
          throw 'Ocorreu um erro inesperado!';
        }

        router.replace('/confirm?method=email');
      })
      .catch((err) => {
        console.error(err);
        const [title, msg] = handleOTPError(err);
        errorToast(msg, title);
      })
      .finally(() => setLoading(false));
  }
}
